import React from "react";
import { EuiIcon, IconType } from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";

/*
 * Operating models - "run it your way".
 *
 * opsanio ships as one appliance (a physical OR virtual machine running k3s)
 * that can be consumed three ways. Honest framing per the deployment docs:
 *   - Fully Managed Service - operated by the opsanio team (HW + ops + updates).
 *   - On-Premise - your own infrastructure; data stays in-house, up to a 100 %
 *     offline Dark Site (updates on encrypted USB media).
 *   - Cloud - the same virtual appliance in your own cloud / hosting tenancy.
 *
 * Below the cards: the support tiers from the contract terms (Ziffer 8) -
 * Standard (Bronze), Enterprise (Silber) and 24x7 (agreed separately).
 *
 * Placed high (marketing layer) - a key buying decision - before the deep
 * technical capability cards. Icon cards, i18n with EN + DE fallbacks.
 */

const tp = "anketa.autonubil.home.models";

interface Model {
  slug: string;
  icon: IconType;
  title: string;
  description: string;
  points: string[];
}

const Check: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }}>
    <path d="M5 13l4 4L19 7" stroke="var(--ak-accent)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ModelCard: React.FC<{ model: Model; featured?: boolean }> = ({ model, featured }) => {
  // Not clickable -> deliberately no hover effect (a hover would imply a link).
  return (
    <div
      style={{
        background: "var(--ak-surface)",
        border: `1px solid ${featured ? "var(--ak-accent-border)" : "var(--ak-border)"}`,
        borderRadius: 16,
        padding: "32px 28px",
        display: "flex",
        flexDirection: "column",
        boxShadow: featured
          ? "0 6px 20px var(--ak-accent-tint-strong)"
          : "0 1px 4px var(--ak-shadow)",
        position: "relative",
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 12,
          background: "var(--ak-accent-tint)",
          border: "1px solid var(--ak-accent-border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 18,
        }}
      >
        <EuiIcon type={model.icon} size="l" color="primary" />
      </div>
      <h3
        style={{
          margin: 0,
          fontSize: "1.25rem",
          fontWeight: 700,
          letterSpacing: "-0.01em",
          color: "var(--ak-text)",
        }}
      >
        {model.title}
      </h3>
      <p style={{ margin: "10px 0 0", fontSize: "0.97rem", lineHeight: 1.6, color: "var(--ak-text-secondary)" }}>
        {model.description}
      </p>
      <ul style={{ listStyle: "none", margin: "18px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 9 }}>
        {model.points.map((p) => (
          <li key={p} style={{ display: "flex", gap: 10, fontSize: "0.9rem", lineHeight: 1.5, color: "var(--ak-text-secondary)", fontWeight: 500 }}>
            <Check />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const AutonubilOperatingModels: React.FC = () => {
  const { translate } = useAnketaI18n();

  const models: Model[] = [
    {
      slug: "managed",
      icon: "gear",
      title: translate(`${tp}.managed.title`, { defaultMessage: "Fully Managed Service" } as any),
      description: translate(`${tp}.managed.description`, {
        defaultMessage:
          "We operate the appliance for you - hardware, updates and SIEM included. No in-house security team required.",
      } as any),
      points: [
        translate(`${tp}.managed.point1`, { defaultMessage: "Operated & maintained by the opsanio team" } as any),
        translate(`${tp}.managed.point2`, { defaultMessage: "Wave-based, auditable updates" } as any),
      ],
    },
    {
      slug: "onprem",
      icon: "database",
      title: translate(`${tp}.onprem.title`, { defaultMessage: "On-Premise" } as any),
      description: translate(`${tp}.onprem.description`, {
        defaultMessage:
          "Runs on your own infrastructure as a sovereign appliance. Your data stays physically in-house - air-gap capable.",
      } as any),
      points: [
        translate(`${tp}.onprem.point1`, { defaultMessage: "Data never leaves your network" } as any),
        translate(`${tp}.onprem.point2`, {
          defaultMessage: "Updates online, via a gateway - or as a Dark Site on encrypted USB media",
        } as any),
      ],
    },
    {
      slug: "cloud",
      icon: "cloudSunny",
      title: translate(`${tp}.cloud.title`, { defaultMessage: "Cloud" } as any),
      description: translate(`${tp}.cloud.description`, {
        defaultMessage:
          "Deploy the same virtual appliance in your own cloud or hosting environment - your tenancy, your control.",
      } as any),
      points: [
        translate(`${tp}.cloud.point1`, { defaultMessage: "Same appliance as a virtual machine" } as any),
        translate(`${tp}.cloud.point2`, { defaultMessage: "Your cloud, your data residency" } as any),
      ],
    },
  ];

  // Support tiers (contract terms, Ziffer 8). Level = filled bars out of three.
  const tiers = [
    {
      slug: "bronze",
      level: 1,
      title: translate(`${tp}.tiers.bronze.title`, { defaultMessage: "Standard" } as any),
      tag: translate(`${tp}.tiers.bronze.tag`, { defaultMessage: "Bronze" } as any),
      desc: translate(`${tp}.tiers.bronze.desc`, { defaultMessage: "Mon-Fri 09:00-17:00" } as any),
    },
    {
      slug: "silver",
      level: 2,
      title: translate(`${tp}.tiers.silver.title`, { defaultMessage: "Enterprise" } as any),
      tag: translate(`${tp}.tiers.silver.tag`, { defaultMessage: "Silver" } as any),
      desc: translate(`${tp}.tiers.silver.desc`, {
        defaultMessage: "Hotline Mon-Fri 08:00-22:00 · priority 1 also outside service hours",
      } as any),
    },
    {
      slug: "24x7",
      level: 3,
      title: translate(`${tp}.tiers.24x7.title`, { defaultMessage: "24×7" } as any),
      tag: "",
      desc: translate(`${tp}.tiers.24x7.desc`, {
        defaultMessage: "Around the clock, including public holidays · agreed separately",
      } as any),
    },
  ];

  return (
    <section
      id="operating-models"
      style={{ background: "var(--ak-surface-2)", padding: "clamp(64px, 9vw, 100px) clamp(24px, 5vw, 48px)" }}
    >
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 44px" }}>
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--ak-accent)",
            }}
          >
            {translate(`${tp}.eyebrow`, { defaultMessage: "Operating models" } as any)}
          </p>
          <h2
            style={{
              margin: "12px 0 0",
              fontSize: "clamp(1.9rem, 3.4vw, 2.8rem)",
              fontWeight: 800,
              letterSpacing: "-0.025em",
              lineHeight: 1.1,
              color: "var(--ak-text)",
            }}
          >
            {translate(`${tp}.title`, { defaultMessage: "Run it your way" } as any)}
          </h2>
          <p style={{ margin: "16px auto 0", fontSize: "1.05rem", lineHeight: 1.65, color: "var(--ak-text-secondary)" }}>
            {translate(`${tp}.subtitle`, {
              defaultMessage:
                "One platform, three ways to operate it - sovereign by default, whichever you choose.",
            } as any)}
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
            gap: 24,
            alignItems: "stretch",
          }}
        >
          {models.map((m) => (
            <ModelCard key={m.slug} model={m} featured={m.slug === "managed"} />
          ))}
        </div>

        {/* support tiers */}
        <p
          style={{
            margin: "44px 0 14px",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--ak-accent)",
          }}
        >
          {translate(`${tp}.tiers.eyebrow`, { defaultMessage: "Support tiers" } as any)}
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
            gap: 24,
          }}
        >
          {tiers.map((t) => (
            <div
              key={t.slug}
              style={{
                background: "var(--ak-surface)",
                border: "1px solid var(--ak-border)",
                borderRadius: 14,
                padding: "18px 22px 20px",
                boxShadow: "0 1px 4px var(--ak-shadow)",
              }}
            >
              <div aria-hidden="true" style={{ display: "flex", gap: 5 }}>
                {[1, 2, 3].map((i) => (
                  <span
                    key={i}
                    style={{
                      width: 26,
                      height: 5,
                      borderRadius: 3,
                      background: i <= t.level ? "var(--ak-accent)" : "var(--ak-border)",
                    }}
                  />
                ))}
              </div>
              <h3 style={{ margin: "12px 0 0", fontSize: "1.1rem", fontWeight: 700, color: "var(--ak-text)" }}>
                {t.title}
                {t.tag && <span style={{ color: "var(--ak-accent)", fontWeight: 700 }}> · {t.tag}</span>}
              </h3>
              <p style={{ margin: "6px 0 0", fontSize: "0.9rem", lineHeight: 1.5, color: "var(--ak-text-secondary)" }}>
                {t.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AutonubilOperatingModels;
